/**
 * The planar-face contract shared by the export lowerings (prismFaces,
 * wallBodies) and the STL and STEP serializers that consume them.
 *
 * Every face carries face-local 2D rings (outer boundary first, holes after),
 * a mapper into export space, and an outward hint; rings are pre-oriented so
 * the mapped outer ring's winding normal points outward (holes opposite).
 * Coordinates that must weld across faces (leaf junctions, cutout corners,
 * caps) are produced from the same source floats, never re-derived through
 * different arithmetic: that exactness is what makes each solid close into a
 * single watertight body.
 */

import { type Point } from '@kaiizen/kai-floor-plan';
import { type Vec3 } from '../math3d';
import { type SceneSolid, type SceneWall } from './mesh3d';

const MM_PER_FOOT = 304.8;

/** Lengths under this (feet) are degenerate to the export lowerings. */
export const FACE_EPSILON = 1e-6;

/**
 * What the serializers consume: a scene's export records plus the plan point
 * that becomes the file's origin. Every Scene3D is one (a room's, centered on
 * its primary rectangle; an assembled level's, centered on its region).
 */
export interface ExportScene {
  solids: SceneSolid[];
  walls: SceneWall[];
  center: Point;
}

/**
 * A point in export space: plan x, plan y mirrored (so the frame is y-up and
 * standard right-handed math applies), height h. All feet.
 */
export interface SpacePoint {
  x: number;
  y: number;
  h: number;
}

export interface PlanarFace {
  /** Face-local 2D rings: rings[0] the outer boundary, later rings holes. */
  rings: Point[][];
  /** Face-local 2D → export space. Each ring edge maps to a straight line. */
  map: (facePoint: Point) => SpacePoint;
  /** Direction the face's outward normal points (need not be unit length). */
  outward: SpacePoint;
}

/** A plan point in the mirrored export plane. */
export const mirror = (point: Point): Point => ({ x: point.x, y: -point.y });

/** Newell normal of a mapped ring: a robust polygon normal for orientation. */
export const newellNormal = (points: SpacePoint[]): SpacePoint => {
  let x = 0, y = 0, h = 0;
  for (let i = 0; i < points.length; i++) {
    const current = points[i], next = points[(i + 1) % points.length];
    x += (current.y - next.y) * (current.h + next.h);
    y += (current.h - next.h) * (current.x + next.x);
    h += (current.x - next.x) * (current.y + next.y);
  }
  return { x, y, h };
};

const dot3 = (first: SpacePoint, second: SpacePoint): number => first.x * second.x + first.y * second.y + first.h * second.h;

/** A face with every ring wound to the outward contract (holes opposite). */
export const face = (rings: Point[][], map: (facePoint: Point) => SpacePoint, outward: SpacePoint): PlanarFace => ({
  rings: rings.map((ring, ringIndex) => {
    const aligned = dot3(newellNormal(ring.map(map)), outward) >= 0;
    return aligned === (ringIndex === 0) ? ring : [...ring].reverse();
  }),
  map,
  outward,
});

/** Export space centered on `center` and scaled to the files' millimeters, z-up (export space is already y-mirrored). */
export const toCenteredMillimeters =
  (center: Point) => (point: SpacePoint): Vec3 => [(point.x - center.x) * MM_PER_FOOT, (point.y + center.y) * MM_PER_FOOT, point.h * MM_PER_FOOT];
